// History (design §13): the versions a Review left behind.
//
// A list, newest first, not a graph. Opening one shows the text as it was with
// what changed to make it, and Restore puts the document — and the advisor's
// understanding of it — back that way. Restore appends rather than rewinds, so
// it asks for no confirmation: nothing it does is lost work.

import { useCallback, useEffect, useState } from "react";

import { ApiError, api } from "../api/client";
import { when } from "../advisor/CommentCard";
import { type Piece, wordDiff } from "../history/diff";
import { useDocument } from "./documentContext";

export interface VersionSummary {
  id: string;
  label: string;
  run_id: string | null;
  paragraph_count: number;
  chars_added: number;
  chars_removed: number;
  created_at: string;
  is_head: boolean;
}

interface DiffRow {
  id: string;
  text: string;
  status: "added" | "changed" | "unchanged" | "removed";
  was: string | null;
}

interface VersionDetail extends VersionSummary {
  diff: DiffRow[];
  changes: { added: number; changed: number; removed: number };
  compared_with: { id: string; label: string; created_at: string } | null;
  notes: number;
  comments: number;
}

interface Props {
  documentId: string;
  /** Bumped when a Review commits, so a new version appears without a reload. */
  bump: number;
  /** The document has been put back; the screen reloads it. */
  onRestored: () => void;
  onNotice: (message: string) => void;
}

export function History({ documentId, bump, onRestored, onNotice }: Props) {
  const [versions, setVersions] = useState<VersionSummary[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [detail, setDetail] = useState<VersionDetail | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setVersions(await api.get<VersionSummary[]>(`/api/documents/${documentId}/versions`));
    } catch {
      setVersions([]);
    }
  }, [documentId]);

  useEffect(() => {
    void load();
  }, [load, bump]);

  useEffect(() => {
    if (open === null) return void setDetail(null);
    let cancelled = false;
    api
      .get<VersionDetail>(`/api/versions/${open}`)
      .then((v) => !cancelled && setDetail(v))
      .catch(() => !cancelled && onNotice("Could not open that version."));
    return () => {
      cancelled = true;
    };
  }, [open, onNotice]);

  const restore = async (version: VersionSummary) => {
    setBusy(true);
    try {
      await api.post(`/api/versions/${version.id}/restore`, {});
      onRestored();
      onNotice(`Put back as it was ${when(version.created_at)}`);
      setOpen(null);
      await load();
    } catch (e) {
      onNotice(e instanceof ApiError ? e.message : "Could not restore that version.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (version: VersionSummary) => {
    setBusy(true);
    try {
      await api.delete(`/api/versions/${version.id}`);
      if (open === version.id) setOpen(null);
      await load();
    } catch (e) {
      onNotice(e instanceof ApiError ? e.message : "Could not delete that version.");
    } finally {
      setBusy(false);
    }
  };

  if (versions === null) return <p className="muted small">Loading…</p>;
  if (versions.length === 0) {
    return (
      <div className="history">
        <h2>History</h2>
        <p className="muted small">
          Nothing yet. Every review leaves a version here, with the text as it read it.
        </p>
      </div>
    );
  }

  return (
    <div className="history">
      <h2>History</h2>
      <ol className="plain versions">
        {versions.map((version) => (
          <li key={version.id} className={`version${open === version.id ? " open" : ""}`}>
            <button
              type="button"
              className="version-head"
              onClick={() => setOpen((v) => (v === version.id ? null : version.id))}
            >
              <span className="version-label">
                {version.label}
                {version.is_head && <span className="version-now"> now</span>}
              </span>
              <span className="muted small">
                {when(version.created_at)} · {size(version)}
              </span>
            </button>

            {open === version.id && (
              <div className="version-detail">
                {detail === null || detail.id !== version.id ? (
                  <p className="muted small">Loading…</p>
                ) : (
                  <>
                    <p className="muted small">
                      {detail.compared_with
                        ? `Against "${detail.compared_with.label}": ${changes(detail.changes)}`
                        : "The first version: nothing to compare it with."}
                      {detail.comments > 0 &&
                        ` · ${detail.comments} comment${detail.comments === 1 ? "" : "s"} open then`}
                    </p>

                    <div className="version-text">
                      {detail.diff.map((row) => (
                        <Row key={row.id} row={row} />
                      ))}
                    </div>

                    <div className="version-actions">
                      <button
                        type="button"
                        className="text-button"
                        disabled={busy}
                        onClick={() => void restore(version)}
                      >
                        Put the document back to this
                      </button>
                      {!version.is_head && (
                        <button
                          type="button"
                          className="text-button quiet"
                          disabled={busy}
                          onClick={() => void remove(version)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </li>
        ))}
      </ol>
      <p className="muted small">
        Restoring adds a version rather than erasing one, so nothing here is ever lost.
      </p>
    </div>
  );
}

function Row({ row }: { row: DiffRow }) {
  if (row.status === "removed") {
    return (
      <p className="version-para removed">
        <del>{row.was}</del>
      </p>
    );
  }
  if (row.status === "changed" && row.was !== null) {
    return (
      <p className="version-para changed">
        {wordDiff(row.was, row.text).map((piece, i) => (
          <Mark key={i} piece={piece} />
        ))}
      </p>
    );
  }
  return <p className={`version-para ${row.status}`}>{row.text || " "}</p>;
}

function Mark({ piece }: { piece: Piece }) {
  if (piece.kind === "same") return <>{piece.text}</>;
  if (piece.kind === "added") return <ins>{piece.text}</ins>;
  return <del>{piece.text}</del>;
}

function size(version: VersionSummary): string {
  const parts: string[] = [];
  if (version.chars_added) parts.push(`+${version.chars_added}`);
  if (version.chars_removed) parts.push(`−${version.chars_removed}`);
  const measure = parts.length > 0 ? parts.join(" ") : "no change in length";
  return `${version.paragraph_count} paragraph${version.paragraph_count === 1 ? "" : "s"} · ${measure}`;
}

function changes(counts: VersionDetail["changes"]): string {
  const parts: string[] = [];
  if (counts.changed) parts.push(`${counts.changed} changed`);
  if (counts.added) parts.push(`${counts.added} new`);
  if (counts.removed) parts.push(`${counts.removed} deleted`);
  return parts.length > 0 ? parts.join(", ") : "nothing changed";
}

/** History as a route under the document (design §14.4). */
export function HistoryRoute() {
  const { documentId, advisorBump, onRestored, onNotice } = useDocument();
  return (
    <History
      documentId={documentId}
      bump={advisorBump}
      onRestored={onRestored}
      onNotice={onNotice}
    />
  );
}
